// Author: Sunil Jain
// File: e1c/src/components/NotificationList.js
// Description: Dropdown component to display a list of recent notifications.
// Updated: 2025-06-15 by Gemini - Added dynamic link generation for actionable notifications.

import React from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, Typography, Divider, Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { PeopleAlt as PeopleIcon, MenuBook as LibraryBooksIcon, Assignment as TaskIcon, WarningAmber as WarningAmberIcon } from '@mui/icons-material';

/**
 * Generates an icon based on the notification type.
 */
const getNotificationIcon = (type) => {
  // FIX: Use the actual enum values from the backend model.
  switch (type) {
    case 'message_new': return <PeopleIcon fontSize="small" />;
    case 'reminder_assignment': return <TaskIcon fontSize="small" />;
    case 'alert_threshold': return <WarningAmberIcon fontSize="small" />; // Example for another type
    default: return <LibraryBooksIcon fontSize="small" />; // A more generic default
  }
};

/**
 * Generates a navigation link based on the notification's related entity.
 */
const getNotificationLink = (notification) => {
    if (!notification.related_entity_type || !notification.related_entity_id) {
        return '/cockpit'; // Default link if no entity is related
    }
    switch (notification.related_entity_type) {
        case 'patient':
            return `/patients/${notification.related_entity_id}`;
        // Future entity types can be added here
        // case 'assignment':
        //     return `/assignments/${notification.related_entity_id}`;
        default:
            return '/cockpit';
    }
};

const NotificationList = ({ notifications, anchorEl, open, onClose }) => {
  return (
    <Menu
      id="notifications-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      MenuListProps={{ 'aria-labelledby': 'notifications-button' }}
      PaperProps={{
        style: { maxHeight: 400, width: '360px' },
        sx: { mt: 1.5, '& .MuiAvatar-root': { width: 32, height: 32, ml: -0.5, mr: 1, }, '&:before': { content: '""', display: 'block', position: 'absolute', top: 0, right: 14, width: 10, height: 10, bgcolor: 'background.paper', transform: 'translateY(-50%) rotate(45deg)', zIndex: 0, }, },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <Box px={2} py={1}>
        <Typography variant="subtitle1">Notifications</Typography>
      </Box>
      <Divider sx={{ borderStyle: 'dashed' }}/>
      
      {(!notifications || notifications.length === 0) && (
        <MenuItem onClick={onClose}>
          <ListItemText primary="No new notifications" />
        </MenuItem>
      )}

      {notifications && notifications.map((notification) => (
        <MenuItem
          key={notification.id}
          onClick={onClose}
          component={RouterLink}
          to={getNotificationLink(notification)} // Use the dynamic link
          sx={{ whiteSpace: 'normal', py: 1.5 }}
        >
          <ListItemIcon>{getNotificationIcon(notification.notification_type)}</ListItemIcon>
          <ListItemText
            primary={notification.title}
            secondary={notification.body}
            primaryTypographyProps={{ fontWeight: notification.is_read ? 'normal' : 'bold', typography: 'subtitle2' }}
            secondaryTypographyProps={{ typography: 'body2', color: 'text.secondary' }}
          />
        </MenuItem>
      ))}
      
      <Divider sx={{ borderStyle: 'dashed' }}/>
      <Box sx={{ p: 1 }}>
        <MenuItem onClick={onClose} component={RouterLink} to="/notifications" sx={{ borderRadius: 1 }}>
            <ListItemText primary="View All Notifications" sx={{ textAlign: 'center' }}/>
        </MenuItem>
      </Box>
    </Menu>
  );
};

export default NotificationList;